// @flow
import 'isomorphic-fetch';
import React, { Suspense } from 'react';
import injectSheet from 'react-jss';
import classNames from 'classnames';
import Slot from 'shared/editor/Slot';
import SocialIcon from 'shared/components/SocialIcon';
import PostCard from 'shared/components/PostCard';
import translations from 'shared/strings';
import { formatDate, formatDateCN } from 'shared/lib/dateUtils';
import type { $Slot } from '../../../types';
import FeaturedPost from './components/FeaturedPost';
import { getAPIDomain, getWebDomain } from 'shared/lib/clientSettings';
import Icon from '@ff/ui/Icon';
import withThemeProvider from 'shared/hoc/withThemeProvider';
import Loader from './Loader';
import {
  FONT_FAMILY_ROBOTO
} from 'shared/constants';

const Slider = React.lazy(() => import(/* webpackChunkName: "react-slick" */ 'react-slick'));

declare var IS_NODE: boolean;

if (!IS_NODE) {
  // Blocking request to avoid layout shifting
  require('slick-carousel/slick/slick.css'); // eslint-disable-line global-require
  require('slick-carousel/slick/slick-theme.css'); // eslint-disable-line global-require
}

type TemplateSettings = {
  region: string
}

type Props = {
  classes: Object,
  slots: $Slot[],
  templateSettings: TemplateSettings,
  widgetPresets: Array,
  path: string
}

class FutureSightLanding extends React.Component {
  props: Props = this.props;

  state = {
    featuredPosts: [],
    otherPosts: [],
    activeSlide: 0,
    expanded: false
  }

  fetchDomain = ''

  componentDidMount = () => {
    const { templateSettings } = this.props;
    const fetchDomain = getAPIDomain(templateSettings.region) || '';

    // Fetch data for featured posts
    fetch(`${fetchDomain}/api/futuresight/postlist?region=${templateSettings.region}&featured=true`)
      .then(res => res.json())
      .then((res) => {
        this.setState({
          featuredPosts: res.pages
        });
      })
      .catch(() => {
      });
    fetch(`${fetchDomain}/api/futuresight/postlist?region=${templateSettings.region}&featured=false`)
      .then(res => res.json())
      .then((res) => {
        this.setState({
          otherPosts: res.pages
        });
      })
      .catch(() => {
      });
  }

  render() {
    const {
      classes: c, slots, templateSettings, widgetPresets, path
    } = this.props;
    const {
      featuredPosts, otherPosts, activeSlide, expanded
    } = this.state;
    const sliderSettings = {
      arrows: false,
      dots: true,
      dotsClass: 'slick-dots',
      infinite: true,
      speed: 800,
      easing: 'ease-in-out',
      slidesToShow: 1,
      slidesToScroll: 1,
      initialSlide: 0,
      autoplay: true,
      autoplaySpeed: 8000,
      draggable: false,
      afterChange: (i) => {
        this.setState({ activeSlide: i });
      }
    };
    const iconStyle = {
      fontSize: '20px',
      color: 'white',
      marginLeft: '8px',
      marginRight: '8px',
      textDecoration: 'none',
      cursor: 'pointer'
    };

    const domain = getWebDomain(templateSettings.region);

    /* react-slick sometimes sets activeSlide to -1 */
    const slideIndex = (activeSlide >= 0 && activeSlide < featuredPosts.length) ?
      activeSlide : 0;
    const heroCarousel = featuredPosts.length > 0 && (
      <div>
        <div className={c.date}>
          {
            templateSettings.region === 'CN' ?
              formatDateCN(featuredPosts[slideIndex].template.props.published_at) :
              formatDate(featuredPosts[slideIndex].template.props.published_at)
          }
        </div>
        <a
          className={c.postTitle}
          href={featuredPosts[slideIndex].template.props.url}
        >
          <div>
            {featuredPosts[slideIndex].template.props.title}
          </div>
        </a>
        <a
          className={c.readMore}
          href={featuredPosts[slideIndex].template.props.url}
        >
          {translations.READ_MORE[templateSettings.region]}
          <span className="material-icons">arrow_forward</span>
        </a>
        <div className={c.socialShare}>
          <Icon
            name="ff-icon-share"
            className={c.socialIcon}
            onMouseEnter={() => this.setState({ expanded: true })}
            onMouseLeave={() => this.setState({ expanded: false })}
          />
          <div
            className={classNames(c.iconList, expanded && c.expanded)}
            onMouseEnter={() => this.setState({ expanded: true })}
            onMouseLeave={() => this.setState({ expanded: false })}
          >
            {featuredPosts[slideIndex].template.props.social_share.map((link, index) => (
              <SocialIcon
                key={index}
                iconStyle={iconStyle}
                link={link}
                generateQRCode={`${domain}${featuredPosts[slideIndex].template.props.url}`}
              />
            ))}
          </div>
        </div>
      </div>
    );

    return (
      <div className={c.container}>
        <div className={c.menu}>
          <Slot id="menu-bar" slots={slots} widgetPresets={widgetPresets} path={path} />
        </div>
        <div className={c.content}>
          <div className={c.heroSection}>
            <div className={c.heroTitle}>
              <div className={c.futuresightTitle}>
                <div className={c.tagline}>The Official FF Blog</div>
                <div className={c.pageTitle}>Futuresight</div>
              </div>
              <div className={c.seperator} />
              {heroCarousel}
            </div>
            { /*
              Add loader for:
              - Fetching featured posts request is in-flight
              - react-slick chunk download is still in progress
            */ }
            {
              featuredPosts?.length > 0 ? (
                <Suspense fallback={<Loader />}>
                  <Slider {...sliderSettings}>
                    { featuredPosts.map((post, i) => <FeaturedPost key={i} post={post} />) }
                  </Slider>
                </Suspense>
              ) : <Loader />
            }
          </div>
          <div className={c.otherPosts}>
            {otherPosts.length > 0 && otherPosts.map((post, i) => (
              <PostCard post={post.template.props} key={i} />
            ))}
          </div>
        </div>
        <footer id="footer" className={c.footer}>
          <Slot id="footer" slots={slots} widgetPresets={widgetPresets} path={path} />
        </footer>
      </div>
    );
  }
}

const styles = {
  menu: {
    width: '100%',
    position: 'relative',
    zIndex: '10'
  },
  footer: {
    width: '100%'
  },
  heroSection: {
    width: '100%',
    height: '75%',
    minHeight: '560px', /* Keep min-height the same as FutureSightLanding - imageLink */
    position: 'relative',
    fontFamily: FONT_FAMILY_ROBOTO,
    '& .slick-list': {
      height: '100%'
    },
    '& .slick-dots': {
      bottom: '15px',
    },
    '& .slick-dots li': {
      width: '10px',
      height: '10px'
    },
    '& .slick-dots li button:before': {
      fontSize: '10px',
      transition: 'all .2s ease',
      color: 'white'
    },
    '& .slick-dots li.slick-active button:before': {
      fontSize: '12px',
      color: 'white'
    },
    '@media(max-width: 960px)': {
      width: '100%',
      height: '100vh',
      minHeight: '100%',
      '& .slick-dots': {
        bottom: '12px'
      }
    },
    '@media(max-height: 701px) and (max-width: 960px)': {
      height: '700px'
    }
  },
  heroTitle: {
    width: '30%',
    height: '100%',
    backgroundColor: '#141618',
    position: 'absolute',
    left: '0',
    top: '0',
    zIndex: '3',
    color: 'white',
    padding: '84px 36px 54px',
    minHeight: '200px',
    '@media(max-width: 1200px)': {
      width: '40%'
    },
    '@media(max-width: 960px)': {
      left: '0',
      right: '0',
      bottom: '0',
      top: 'auto',
      width: '100%',
      height: '40vh',
      padding: '18px 36px',
      minHeight: 0
    },
    '@media(max-height: 701px)': {
      minHeight: '300px'
    }
  },
  tagline: {
    fontSize: '15px',
    fontWeight: '600',
    letterSpacing: '.1em',
    color: 'white',
    textTransform: 'uppercase'
  },
  pageTitle: {
    fontSize: '46px',
    fontFamily: 'Sectra',
    letterSpacing: '1.15px',
    color: 'white',
    marginTop: '18px',
    textTranform: 'uppercase'
  },
  seperator: {
    height: '2px',
    width: '90px',
    backgroundColor: '#949599',
    marginTop: '18px',
    marginBottom: '27px',
    '@media(max-width: 960px)': {
      display: 'none'
    }
  },
  date: {
    marginTop: '18px',
    fontSize: '15px',
    fontWeight: '600',
    letterSpacing: '.1em',
    lineHeight: '1.75em',
    textTransform: 'uppercase'
  },
  postTitle: {
    fontSize: '34px',
    fontWeight: '300',
    marginTop: '18px',
    lineHeight: '1.5em',
    textDecoration: 'none',
    display: '-webkit-box',
    lineClamp: '4',
    boxOrient: 'vertical',
    overflow: 'hidden',
    color: 'white',
    '&:hover': {
      color: '#949599'
    },
    /*
      Media queries that match both max-height and max-width.
      Adjust line clamp for mobile or tablet screen sizes.
    */
    '@media(max-height: 769px) and (max-width: 960px)': {
      lineClamp: '2',
    },
    '@media(max-height: 960px) and (max-width: 960px)': {
      lineClamp: '3',
    },
    /* max-width queries */
    '@media(max-width: 960px)': {
      fontSize: '27px',
    },
    '@media(max-width: 577px)': {
      fontSize: '21px',
    },
  },
  readMore: {
    textDecoration: 'none',
    color: '#949599',
    display: 'flex',
    alignItems: 'center',
    fontSize: '12px',
    fontWeight: '600',
    letterSpacing: '.1em',
    marginTop: '18px',
    textTransform: 'uppercase',
    cursor: 'pointer',
    '& > .material-icons': {
      fontSize: '14px',
      marginLeft: '5px'
    }
  },
  otherPosts: {
    padding: '49px 36px',
    display: 'flex',
    flexWrap: 'wrap',
    background: '#f7f7f7',
    '@media(max-width: 768px)': {
      padding: '36px 18px'
    }
  },
  cardArticle: {
    marginTop: '36px',
    marginBottom: '36px',
    paddingBottom: '36px',
    width: '33.3%',
    paddingLeft: '18px',
    paddingRight: '18px',
    fontFamily: FONT_FAMILY_ROBOTO,
    '@media(max-width: 960px)': {
      width: '50%'
    },
    '@media(max-width: 768px)': {
      width: '100%',
      paddingLeft: '0',
      paddingRight: '0',
      paddingBottom: '0',
      marginBottom: '0'
    }
  },
  cardHeroImg: {
    width: '100%',
    height: '20%',
    maxHeight: '194px',
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
    backgroundPosition: 'center center'
  },
  cardContent: {
    padding: '18px 36px',
    backgroundColor: 'white',
    '@media(max-width: 768px)': {
      paddingLeft: '18px',
      paddingRight: '18px'
    }
  },
  cardDate: {
    fontSize: '12px',
    fontWeight: '600',
    lineHeight: '1.75em',
    padding: '18 0',
    textTransform: 'uppercase'
  },
  cardTitle: {
    fontSize: '23px',
    fontWeight: '300',
    letterSpacing: '.025em',
    lineHeight: '1.5em',
    transition: 'all .2s ease',
    paddingBottom: '18px',
    borderBottom: '1px solid #eaeaea',
    cursor: 'pointer',
    '& > a': {
      color: 'black',
      textDecoration: 'none'
    },
  },
  socialShare: {
    padding: '18px 0',
    display: 'flex',
    alignItems: 'center'
  },
  iconList: {
    width: '0',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    transition: 'all .4s ease'
  },
  expanded: {
    width: '100%',
    overflow: 'visible',
    animation: '.4s $removeHiddenOverflow'
  },
  '@keyframes removeHiddenOverflow': {
    from: {
      overflow: 'hidden'
    }
  },
  socialIcon: {
    cursor: 'pointer',
    fontSize: '15px',
    lineHeight: '1.2em',
    marginLeft: '-4px',
    padding: '5px',
  },
  cardDesc: {
    paddingBottom: '36px',
    fontSize: '15px',
    lineHeight: '1.75em'
  },
  cardReadMore: {
    display: 'block',
    paddingBottom: '18px',
    fontSize: '12px',
    textDecoration: 'none',
    fontWeight: '600',
    letterSpacing: '.1em',
    lineHeight: '1.75em',
    color: 'black',
    textTransform: 'uppercase',
    cursor: 'pointer',
  },
  cardTags: {
    padding: '18px 0',
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    fontFamily: FONT_FAMILY_ROBOTO,
    fontSize: '12px',
    fontWeight: '600',
    lineHeight: '1.75em',
    borderBottom: '1px solid #eaeaea',
    '& > a': {
      textDecoration: 'none',
      color: 'black',
      fontSize: '12px',
      fontWeight: '600',
      lineHeight: '1.75em',
      marginRight: '5px',
      transition: 'all .2s ease',
      '&:after': {
        content: '","',
        display: 'inline-block'
      },
      '&:last-child:after': {
        content: '""'
      }
    }
  },
  futuresightTitle: {
    '@media(max-width: 960px)': {
      display: 'none'
    }
  }
};

export default withThemeProvider(injectSheet(styles)(FutureSightLanding));
